import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiSave } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useAdminAccess } from '../utilities/adminAccess';
import {
  extractNewsEntities,
  extractNewsItem,
  getNewsImageUrl,
  isNewsEnabled,
  newsTextToHtml,
  htmlToMarkdown,
  parseNewsTags,
  serializeNewsEntities,
  serializeNewsTagKeys,
} from '../utilities/newsApi';
import AdminPhotoUpload from '../components/AdminPhotoUpload';
import CustomModal from '../components/CustomModal';
import NewsEntityPicker from '../components/NewsEntityPicker';
import NewsRichEditor from '../components/NewsRichEditor';
import NewsTagPicker from '../components/NewsTagPicker';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/usersManagement.css';
import '../styles/newsManagement.css';

const emptyForm = {
  id: null,
  enabled: true,
  title: '',
  preview: '',
  tags: [],
  entities: [],
  image: '',
  imageData: '',
  text: '',
  created_at: '',
  updated_at: '',
};

const formFromItem = (item) => ({
  id: item.id || null,
  enabled: isNewsEnabled(item.enabled),
  title: item.title || '',
  preview: item.preview || '',
  tags: parseNewsTags(item.tags),
  entities: extractNewsEntities(item),
  image: item.image || '',
  imageData: '',
  text: newsTextToHtml(item.text || ''),
  created_at: item.created_at || '',
  updated_at: item.updated_at || '',
});

export default function ManageNews() {
  const { newsId } = useParams();
  const isNew = !newsId || newsId === 'new';
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useI18n();
  const { canWrite } = useAdminAccess('news');

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [photoError, setPhotoError] = useState('');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteVisible, setDeleteVisible] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  useEffect(() => {
    if (isNew) {
      setForm(emptyForm);
      setErrors({});
      setPhotoError('');
      setMessage({ text: '', type: '' });
      setLoading(false);
      return;
    }
    loadItem();
  }, [newsId]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
  };

  const loadItem = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    setErrors({});
    setPhotoError('');
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminNewsItem, {
        id: Number(newsId),
      });
      const item = extractNewsItem(response.data);
      if (!item) {
        setForm(emptyForm);
        setMessage({ text: t('newsNotFound'), type: 'error' });
      } else {
        setForm(formFromItem(item));
      }
    } catch (error) {
      setForm(emptyForm);
      setMessage({ text: getApiErrorMessage(error, t('newsLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const next = {};
    if (!form.title.trim()) next.title = t('newsNeedTitle');
    setErrors(next);
    return !Object.keys(next).length;
  };

  const handleSave = async () => {
    if (!canWrite || !validate()) return;
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const body = {
        title: form.title.trim(),
        preview: form.preview.trim().slice(0, 255),
        text: htmlToMarkdown(form.text),
        tags: serializeNewsTagKeys(form.tags),
        entities: serializeNewsEntities(form.entities),
        enabled: form.enabled ? 1 : 0,
        type: 3,
      };
      if (form.imageData) body.image = form.imageData;
      else if (form.image) body.image = form.image;
      else if (!isNew) body.image = '';

      const url = isNew ? API_URLS.addAdminNews : API_URLS.changeAdminNews;
      if (!isNew) body.id = Number(newsId);
      const response = await authorizedPost(dispatch, url, body);
      const saved = extractNewsItem(response.data);
      setMessage({ text: isNew ? t('newsCreated') : t('newsSaved'), type: 'success' });
      if (isNew && saved?.id) {
        history.replace(`/news/${saved.id}`);
      } else if (!isNew && saved) {
        setForm(formFromItem(saved));
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('newsSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canWrite || isNew) return;
    setDeleting(true);
    try {
      await authorizedPost(dispatch, API_URLS.deleteAdminNews, { id: Number(newsId) });
      history.push('/news');
    } catch (error) {
      setDeleting(false);
      setDeleteVisible(false);
      setMessage({ text: getApiErrorMessage(error, t('newsDeleteError')), type: 'error' });
    }
  };

  const photoPreview = form.imageData || getNewsImageUrl(
    form.image,
    form.updated_at || form.created_at || form.id,
  );

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs
              items={[
                { label: t('navNews'), to: '/news' },
                { label: isNew ? t('newsNew') : (form.title.trim() || t('newsEdit')) },
              ]}
            />
          </div>
        </div>

        <div className="st-scroll">
          <div className="st-content">
            {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

            {loading ? (
              <div className="st-loading">
                <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                <p>{t('newsLoadingCard')}</p>
              </div>
            ) : (
              <div className="st-block">
                <div className="st-form-grid">
                  <div className="st-field st-field-wide">
                    <label className={`st-check ${!canWrite ? 'is-disabled' : ''}`}>
                      <input
                        type="checkbox"
                        checked={form.enabled}
                        disabled={!canWrite}
                        onChange={(event) => updateField('enabled', event.target.checked)}
                      />
                      {t('stShowOnSite')}
                    </label>
                  </div>

                  <div className="st-field st-field-wide">
                    <label>{t('newsTitle')} <span className="st-required">*</span></label>
                    <input
                      type="text"
                      className={`st-input ${errors.title ? 'error' : ''}`}
                      value={form.title}
                      disabled={!canWrite}
                      placeholder={t('newsTitlePh')}
                      onChange={(event) => updateField('title', event.target.value)}
                    />
                    {errors.title ? <span className="st-field-error">{errors.title}</span> : null}
                  </div>

                  <div className="st-field st-field-wide">
                    <label>{t('newsPreview')}</label>
                    <input
                      type="text"
                      className="st-input"
                      value={form.preview}
                      disabled={!canWrite}
                      maxLength={255}
                      placeholder={t('newsPreviewPh')}
                      onChange={(event) => updateField('preview', event.target.value)}
                    />
                  </div>

                  <div className="st-field st-field-wide">
                    <label>{t('newsTags')}</label>
                    <NewsTagPicker
                      value={form.tags}
                      onChange={(next) => updateField('tags', next)}
                      disabled={!canWrite}
                    />
                  </div>

                  <div className="st-field st-field-wide">
                    <label>{t('newsEntities')}</label>
                    <NewsEntityPicker
                      value={form.entities}
                      onChange={(next) => updateField('entities', next)}
                      disabled={!canWrite}
                    />
                  </div>

                  <div className="st-field news-cover-photo">
                    <label>{t('stPhoto')}</label>
                    <AdminPhotoUpload
                      preview={photoPreview}
                      error={photoError}
                      disabled={!canWrite}
                      onFileChange={(file, error, dataUrl) => {
                        setPhotoError(error || '');
                        if (error || !dataUrl) return;
                        setForm((prev) => ({ ...prev, imageData: dataUrl }));
                      }}
                    />
                    {canWrite && (form.image || form.imageData) ? (
                      <button
                        type="button"
                        className="news-photo-clear"
                        onClick={() => {
                          setForm((prev) => ({ ...prev, image: '', imageData: '' }));
                          setPhotoError('');
                        }}
                      >
                        {t('stPhotoRemove')}
                      </button>
                    ) : null}
                  </div>

                  <div className="st-field st-field-wide">
                    <label>{t('newsText')}</label>
                    <NewsRichEditor
                      value={form.text}
                      disabled={!canWrite}
                      placeholder={t('newsTextPh')}
                      onChange={(html) => updateField('text', html)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {canWrite && !loading ? (
          <div className={`st-actions news-edit-actions ${!isNew ? 'st-actions-split' : ''}`}>
            <div className="st-actions-group">
              {!isNew ? (
                <button
                  type="button"
                  className="st-btn st-btn-danger"
                  disabled={saving || deleting}
                  onClick={() => setDeleteVisible(true)}
                >
                  {t('delete')}
                </button>
              ) : null}
              <button
                type="button"
                className="st-btn st-btn-primary"
                disabled={saving}
                onClick={handleSave}
              >
                <FiSave /> {saving ? t('stLoading') : t('newsSave')}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={deleteVisible}
        closeModal={() => setDeleteVisible(false)}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('newsDeleteTitle')}</div>
          <p>{t('newsDeleteText')}</p>
          <div className="st-delete-actions">
            <button type="button" className="btn-modal btn-secondary-modal" onClick={() => setDeleteVisible(false)}>
              {t('cancel')}
            </button>
            <button type="button" className="btn-modal btn-danger-modal" disabled={deleting} onClick={handleDelete}>
              {t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
